/**
 * gd-igc.js — Instant Gels collection page.
 * Claude 2026-08-10, built to Adib's Claude-Design handoff, hardened after the
 * adversarial audit (workflow wy3f54qt4).
 *
 * WHAT THIS RENDERS
 *   · the lavender "Kombiner & spar" kit bar (tier pills + max saving + CTA)
 *   · the chip row  — Alle / Bestselgere / Nyheter / one chip per colour family
 *   · "Sorter: X" dropdown and the "Alle filtre" drawer (5 facet groups)
 *   · the card grid, the quick view, and the kit tray
 *
 * TWO ADD-TO-CART PATHS (since 2026-09-28, "buy one set" fix):
 *   1. THE TRAY writes to the cart directly (submitTray). gd-cart-add.js never
 *      sees those adds, so this file owns their analytics, error surface and the
 *      event pair itself.
 *   2. THE QUICK VIEW'S PRIMARY BUTTON is a real
 *      <form action="/cart/add" data-gd-quick-add data-igc-buy> that
 *      gd-cart-add.js intercepts (toast, Klaviyo, events, L1 guard). This file
 *      dispatches NOTHING for that path. It only listens for the resulting
 *      product-added-to-cart to close the quick view (the toast sits beneath it)
 *      and to drop the bought design from the tray so the tray cannot sell it twice.
 *   Why: shoppers read "add to the kit" plus an unfinished-kit tray as "single
 *   sets are not for sale" (SE customer service and ad comments, Sep 2026).
 *   A single stays a PLAIN retail line on both paths — never add properties.
 *
 * MONEY: never hardcoded, and never rounded to whole units. FI bundle tiers are
 * 99,90 / 124,90 / 144,90 € — rounding produced "100 €" on a button that charges
 * 99,90. Minor units are a per-market setting, like the suffix.
 *
 * SIZING is the theme's, DESIGN is the mock's — see the header of gd-igc.css.
 */
(function () {
  'use strict';

  const host = document.querySelector('[data-gd-igc]');
  if (!host) return;
  const dataEl = host.querySelector('[data-gd-igc-data]');
  if (!dataEl) return;

  // Theme-editor guard: changing a setting re-runs this IIFE without unloading
  // the previous one, which stacked document listeners and a second <body>-level
  // overlay. Stale generations no-op; stale overlays are removed outright.
  const GEN = (window.__gdIgcGen = (window.__gdIgcGen || 0) + 1);
  const mine = () => GEN === window.__gdIgcGen;
  document.querySelectorAll('.gd-igc__qv, .gd-igc__dr').forEach(n => n.remove());

  let DATA;
  try { DATA = JSON.parse(dataEl.textContent); }
  catch (e) { console.error('[gd-igc] catalogue JSON failed to parse', e); return; }

  const DESIGNS = DATA.designs || [];
  const L = DATA.labels || {};
  const QV = DATA.qv || {};

  const elKitBar = host.querySelector('[data-igc-kitbar]');
  const elChips  = host.querySelector('[data-igc-chips]');
  const elSort   = host.querySelector('[data-igc-sort]');
  const elCount  = host.querySelector('[data-igc-count]');
  const elGrid   = host.querySelector('[data-igc-grid]');
  const elEmpty  = host.querySelector('[data-igc-empty]');
  const elInserts = host.querySelector('[data-igc-inserts]');

  const esc = s => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  // Minor units are opt-in per market: NO/SE/DK price in whole units and print
  // "1 099 kr"; FI prices 99,90 €. Padding is unconditional once enabled, so FI
  // shows "110,00 €" where the theme's own `| money` shows "110,00 €" — not "110".
  const money = cents => {
    const c = Math.round(cents || 0);
    const maj = String(Math.floor(c / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const dp = L.decimalPlaces | 0;
    const dec = dp === 0 ? '' : (L.decimalSep || ',') + String(c % 100).padStart(2, '0');
    // The separator is added HERE, not carried as a leading space in the setting.
    // Shopify trims whitespace when a text setting is saved, so " kr" came back
    // as "kr" and every price on all four stores rendered "1 099kr". Trimming
    // and joining explicitly makes the spacing immune to that.
    const suffix = String(L.currencySuffix == null ? 'kr' : L.currencySuffix).trim();
    return maj + dec + (suffix ? ' ' + suffix : '');
  };

  // Metafield values are stored lowercase ("kort", "squoval"); the design shows
  // them sentence-cased. Capitalise for DISPLAY only — never write back, or the
  // filter sets would stop matching the stored values.
  const cap = s => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');
  // `results` and `drawerCta` carry no Liquid `| default:` (a brace inside a
  // quoted default terminates the output tag and 422s the section), so their
  // fallback lives here instead.
  const tpl = (s, fb, vars) => String(s || fb)
    .replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m))
    // A tier with no free items would otherwise leave "+ gratis " hanging.
    .replace(/\s*\+\s*(gratis\s*)?(?=$|[.!?])/i, '')
    .replace(/\s{2,}/g, ' ').trim();

  function emit(name, payload) {
    try {
      document.dispatchEvent(new CustomEvent('gd:event', { detail: { name, payload } }));
      if (window.dataLayer && window.dataLayer.push) window.dataLayer.push({ event: name, ...payload });
    } catch (e) {}
  }

  // A pairing miss silently strips a design of its bundle slot. Costs nothing to
  // say so, and it is the cheapest preflight we have across four stores.
  if (DATA.unpaired && DATA.unpaired.length) {
    console.warn('[gd-igc] designs with no $0 component twin (cannot fill a kit slot):', DATA.unpaired);
  }

  if (!DESIGNS.length) {
    // Its own string. `empty_message` is filter-scoped copy ("no design matches
    // the filters") and would be a lie on an empty or unpublished collection.
    if (elEmpty) {
      elEmpty.innerHTML = `<div class="gd-igc__empty-t">${esc(L.emptyCatalogue)}</div>`;
      elEmpty.hidden = false;
    }
    return;
  }

  /* ── filter model ───────────────────────────────────────────────────────
     Five independent facet groups, exactly the mock's: availability, coverage,
     shape, length, design style. Options OR within a group, AND across groups.
     The chip row on top is a separate single-select quick filter. */
  const FACETS = [
    { key: 'avail',  label: L.specAvail,  opts: ['in', 'out'],
      show: v => (v === 'in' ? L.inStock : L.outOfStock),
      get: d => [d.available ? 'in' : 'out'] },
    { key: 'cover',  label: L.specCover,  get: d => (d.cover ? [d.cover] : []) },
    { key: 'form',   label: L.specForm,   get: d => (d.form ? [d.form] : []), show: cap },
    { key: 'len',    label: L.specLen,    get: d => (d.len ? [d.len] : []),  show: cap },
    { key: 'design', label: L.specDesign, get: d => d.design || [] },
  ];

  const state = {
    sets: FACETS.reduce((o, f) => (o[f.key] = new Set(), o), {}),
    chip: 'all',            // 'all' | 'best' | 'new' | a colour_family value
    sort: 'featured',
    sortOpen: false,
    imgIdx: {},             // per-card gallery index
  };

  // Facets with a FIXED option list (availability) always render both options
  // with their real counts, zero included — that is what the handoff draws, and
  // "Tilgjengelighet / På lager 32 / Utsolgt 0" is information a shopper looks
  // for. Only showing values that happen to exist made the whole group vanish
  // whenever the catalogue was fully in stock, which is the normal case.
  // Metafield-derived facets still list only values the catalogue contains —
  // there, an absent value could never match anything.
  const values = {};
  FACETS.forEach(f => {
    const seen = new Map();
    DESIGNS.forEach(d => f.get(d).forEach(v => { if (v) seen.set(v, (seen.get(v) || 0) + 1); }));
    values[f.key] = f.opts
      ? f.opts.map(v => [v, seen.get(v) || 0])
      : [...seen.entries()].sort((a, b) => b[1] - a[1]);
  });

  const colours = (() => {
    const seen = new Map();
    DESIGNS.forEach(d => { if (d.colour) seen.set(d.colour, (seen.get(d.colour) || 0) + 1); });
    return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(e => e[0]);
  })();
  const anyBest = DESIGNS.some(d => d.isBest);
  const anyNew  = DESIGNS.some(d => d.isNew);

  function matchChip(d) {
    if (state.chip === 'all')  return true;
    if (state.chip === 'best') return !!d.isBest;
    if (state.chip === 'new')  return !!d.isNew;
    return d.colour === state.chip;
  }
  function matchFacets(d) {
    return FACETS.every(f => {
      const sel = state.sets[f.key];
      if (!sel.size) return true;
      return f.get(d).some(v => sel.has(v));
    });
  }
  const activeCount = () => FACETS.reduce((n, f) => n + state.sets[f.key].size, 0);

  function sorted(list) {
    const a = list.slice();
    switch (state.sort) {
      case 'best': return a.sort((x, y) => (y.isBest ? 1 : 0) - (x.isBest ? 1 : 0));
      case 'new':  return a.sort((x, y) => (y.isNew ? 1 : 0) - (x.isNew ? 1 : 0));
      case 'az':   return a.sort((x, y) => x.name.localeCompare(y.name, undefined, { sensitivity: 'base' }));
      case 'za':   return a.sort((x, y) => y.name.localeCompare(x.name, undefined, { sensitivity: 'base' }));
      // Featured = catalogue order, with sold-out designs moved to the end.
      default:     return a.filter(d => d.available).concat(a.filter(d => !d.available));
    }
  }
  const SORTS = [
    { key: 'featured', label: L.sortFeatured },
    anyBest ? { key: 'best', label: L.sortBest } : null,
    anyNew  ? { key: 'new',  label: L.sortNew }  : null,
    { key: 'az', label: L.sortAz },
    { key: 'za', label: L.sortZa },
  ].filter(Boolean);

  /* ── kit tray state ──────────────────────────────────────────────────
     PRICING RULE (Adib's call): the largest tier that fits becomes a real
     bundle; anything beyond it is added as retail singles.

     ONE PARTITION, TWO CONSUMERS. The tray's displayed total and the cart
     payload used to be computed by two functions that disagreed: kitTotal()
     picked the tier from kit.length and split overflow by pick order, while
     buildPayload() picked by component capability — and when too few designs
     had usable components it returned null and the caller re-billed the WHOLE
     kit at retail, after the button had already shown the tier price. That is
     an overcharge, and it is why plan() now exists: whatever it returns is what
     the shopper sees AND what the cart receives. There is no fallback path.

     CART CONTRACT — the part that must not drift:
       bundle parent : tier variant, { Designs, __bundle_id, _kit:'true' }
       bundle slots  : the $0 COMPONENT variant, { __bundle_id, _addon:'true' }
       tier extras   : $0 variants,               { __bundle_id, _addon:'true' }
       overflow      : the RETAIL variant, NO properties at all
     The overflow singles deliberately carry no __bundle_id: gd-cart-sanitizer
     reads any priced line with a __bundle_id as a second bundle parent, and
     gd-tier-gifts excludes __bundle_id lines from gift-eligible spend. Singles
     SHOULD count toward that spend, so they must stay clean. */
  const TIERS = (DATA.tiers || []).map(t => ({ ...t, extras: (t.extras || []).filter(Boolean) }))
    .sort((a, b) => a.count - b.count);
  const SWATCH = DATA.swatches || { hex: {}, label: {} };
  const elTray = host.querySelector('[data-igc-tray]');
  const trayOn = !!elTray && TIERS.length > 0;
  // With no tiers the page still renders perfectly but sells nothing: no card
  // "+", no quick-view CTA, no kit bar, no tray. Both inputs are per-store
  // (a free-text bundle handle, and design_count/availability on its variants),
  // so this is exactly the misconfiguration a four-store rollout produces —
  // and it deserves to be visible on one page load, like the pairing warning.
  if (elTray && !TIERS.length) {
    console.warn('[gd-igc] no bundle tiers resolved — kit tray, card "+" and the kit bar are OFF. ' +
      'Check the bundle_product handle and custom.design_count / availability on its variants.');
  }
  const kit = [];               // design ids, in the order chosen
  let busy = false;

  /* ── tray persistence ───────────────────────────────────────────────────
     The builder used to reset to empty on every page load, so a shopper who
     opened a design and came back started from zero — which made "add one,
     add another, add another" the path of least resistance and produced four
     un-bundled retail lines instead of one kit.

     Keyed on HANDLE, never on the emitted id: the section numbers designs with
     `{{ forloop.index0 }}` AFTER the L1 anti-abuse skip, so the moment one
     product is withdrawn every id below it shifts and a stored index would
     restore the wrong colour. sessionStorage, not local: a kit in progress is
     a within-visit intent, and it must not outlive the tab and surprise
     someone a week later. */
  const PKEY = 'gd-igc-kit:' + location.pathname;
  const PMAX = 24;              // sanity cap; a poisoned value can't flood the tray
  function saveKit() {
    try {
      sessionStorage.setItem(PKEY, JSON.stringify(kit.map(id => (byId(id) || {}).handle).filter(Boolean)));
    } catch (e) {}
  }
  function restoreKit() {
    let hs = [];
    try { hs = JSON.parse(sessionStorage.getItem(PKEY) || '[]'); } catch (e) { return; }
    if (!Array.isArray(hs)) return;
    hs.slice(0, PMAX).forEach(h => {
      const d = DESIGNS.find(x => x.handle === h);
      // Re-check availability on restore: a design can sell out between loads,
      // and a restored tray must never offer something the payload can't buy.
      if (d && d.available && kit.indexOf(d.id) < 0) kit.push(d.id);
    });
  }

  /* ── what is ALREADY in the cart ────────────────────────────────────────
     Read-only. This page recognises its own retail variants, so it can tell a
     shopper who added designs one at a time that the same designs cost less as
     a kit. A line only counts as "loose" if it carries none of the bundle
     properties — an _addon/_kit line is already part of a kit and must never
     be counted, offered, or touched. */
  const RETAIL = new Map();
  DESIGNS.forEach(d => { if (d.variantId) RETAIL.set(d.variantId, d.id); });
  let owned = [];               // [{ key, id }] — ONE ENTRY PER UNIT
  const lineQty = new Map();    // cart line key -> its current quantity
  const paidUpsellSkus = new Set(); // SKUs of paid upsell lines (Remove / Heal) in the cart

  async function readOwned() {
    try {
      const c = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const next = []; lineQty.clear(); paidUpsellSkus.clear();
      (c.items || []).forEach(l => {
        const p = l.properties || {};
        if (p._upsell === 'true' && l.sku) paidUpsellSkus.add(l.sku);
        if (p.__bundle_id || p._kit === 'true' || p._addon === 'true' || p._free_gift === 'true' || p._gd_gift) return;
        const did = RETAIL.get(l.variant_id);
        if (did === undefined) return;
        lineQty.set(l.key, l.quantity);
        for (let i = 0; i < l.quantity; i++) next.push({ key: l.key, id: did });
      });
      owned = next;
    } catch (e) { owned = []; lineQty.clear(); }
  }

  const byId = id => DESIGNS.find(d => d.id === id);
  const tierFor = n => { let t = null; TIERS.forEach(x => { if (n >= x.count) t = x; }); return t; };
  const kitStrike = () => kit.reduce((s, id) => s + ((byId(id) || {}).price || 0), 0);

  // A design may fill a bundle slot only if its twin is a REAL $0 variant and is
  // in stock. componentPrice is emitted by the section's explicit zero-price
  // scan; without it a priced variant could be sent as a "free" slot.
  const canBundle = d => !!d && d.componentVariantId > 0 && d.componentAvailable && d.componentPrice === 0;

  function plan() {
    const bundleable = kit.filter(id => canBundle(byId(id)));
    const rest = kit.filter(id => bundleable.indexOf(id) < 0);
    const t = tierFor(bundleable.length);          // tier capped by usable slots
    if (!t) return { tier: null, chosen: [], overflow: kit.slice(), total: kitStrike(), bundleable: bundleable.length };
    const chosen = bundleable.slice(0, t.count);
    const overflow = bundleable.slice(t.count).concat(rest);
    const total = t.price + overflow.reduce((s, id) => s + ((byId(id) || {}).price || 0), 0);
    return { tier: t, chosen, overflow, total, bundleable: bundleable.length };
  }

  function toggleKit(id) {
    if (!trayOn || busy) return false;
    const i = kit.indexOf(id);
    if (i >= 0) { kit.splice(i, 1); saveKit(); render(); return true; }
    const d = byId(id);
    // Retail availability is the gate to enter the tray at all. Component
    // availability only decides whether a design can occupy a BUNDLE slot.
    if (!d || !d.available) return false;
    kit.push(id);
    saveKit();
    render();
    return true;
  }

  /* ── the kit offer ──────────────────────────────────────────────────────
     Combines what the shopper already has in the cart with what is in the
     tray, and reports whether those same designs are cheaper as a kit.

     DISTINCT designs only. Two units of one design cannot fill two bundle
     slots: they would collapse into a single $0 _addon line at quantity 2,
     and gd-cart-sanitizer force-resets any _addon line above 1 (verified at
     gd-cart-sanitizer.js:141-147) — the shopper would pay the tier price and
     silently lose a slot. Duplicates therefore stay loose singles.

     Nothing here mutates anything. */
  function offer() {
    if (!trayOn || !owned.length) return null;
    const seen = new Set(); const uniq = [];
    // Cart-owned designs come first: they are already paid for, so they are the
    // ones worth converting.
    owned.forEach(o => { if (!seen.has(o.id)) { seen.add(o.id); uniq.push(o.id); } });
    kit.forEach(id => { if (!seen.has(id)) { seen.add(id); uniq.push(id); } });
    const bundleable = uniq.filter(id => canBundle(byId(id)));
    const t = tierFor(bundleable.length);
    if (!t) return null;                                  // still below the smallest tier
    const chosen = bundleable.slice(0, t.count);
    const retail = chosen.reduce((s, id) => s + ((byId(id) || {}).price || 0), 0);
    const save = retail - t.price;
    if (save <= 0) return null;                           // never offer a worse deal
    // Consume ONE owned unit per design that actually lands in the kit. A second
    // unit of the same design, and any design not in `chosen`, is left alone.
    const inKit = new Set(chosen); const used = new Set(); const consume = [];
    owned.forEach(o => {
      if (inKit.has(o.id) && !used.has(o.id)) { used.add(o.id); consume.push(o); }
    });
    // How the offer is WORDED depends on where the designs came from. Saying
    // "you have 6 in your bag" when four are in the bag and two are picked in
    // the tray is exactly the kind of cart-language-for-a-non-cart claim this
    // page was just corrected for. Two counts, reported separately.
    const fromCart = used.size;
    return { tier: t, chosen, save, retail, consume, fromCart, fromTray: chosen.length - fromCart };
  }

  // Bundle group only — no overflow. The offer converts a specific set of
  // designs; anything outside the tier keeps whatever form it already has,
  // so nothing is removed and re-added for no reason.
  function offerPayload(o) {
    const bundleId = 'igc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    const names = o.chosen.map(id => byId(id).name).join(',');
    return [
      { id: o.tier.variantId, quantity: 1, properties: { Designs: names, __bundle_id: bundleId, _kit: 'true' } },
      ...o.chosen.map(id => ({ id: byId(id).componentVariantId, quantity: 1,
                               properties: { __bundle_id: bundleId, _addon: 'true' } })),
      ...o.tier.extras.filter(x => x && x.id).map(x => ({ id: x.id, quantity: Math.max(1, parseInt(x.qty, 10) || 1),
                               properties: { __bundle_id: bundleId, _addon: 'true' } })),
    ];
  }

  /* ── kit bar ───────────────────────────────────────────────────────── */
  function unitPrice() {
    const tally = new Map();
    DESIGNS.forEach(d => tally.set(d.price, (tally.get(d.price) || 0) + 1));
    let best = 0, n = -1;
    tally.forEach((c, p) => { if (c > n) { n = c; best = p; } });
    return best;
  }

  function insertHref() {
    const a = INSERTS.length ? INSERTS[0].node.querySelector('a[href]') : null;
    return a ? a.getAttribute('href') : '';
  }

  function renderKitBar() {
    if (!elKitBar) return;
    if (!TIERS.length || L.kitBarOn === false) { elKitBar.hidden = true; return; }
    const unit = unitPrice();
    const maxSave = TIERS.reduce((m, t) => Math.max(m, t.count * unit - t.price), 0);
    const bestTier = TIERS[TIERS.length - 1];
    const pills = TIERS.map(t => `
      <span class="gd-igc__kb-pill">
        <b class="gd-igc__kb-n">${t.count} ${esc(L.trayUnit || 'design')}</b>
        <span class="gd-igc__kb-p">${esc(money(t.price))}</span>
        ${t === bestTier && L.bestPrice ? `<span class="gd-igc__kb-best">${esc(L.bestPrice)}</span>` : ''}
      </span>`).join('');
    const counts = TIERS.map(t => t.count).join('/');
    // No explicit link set? Borrow the href the "Kombiner & spar" insert already
    // uses, so the bar can never become a button that goes nowhere.
    const href = L.kitUrl || insertHref();
    const cta = href ? `<a class="gd-igc__kb-cta" href="${esc(href)}">${esc(L.buildKit)}</a>` : '';
    elKitBar.hidden = false;
    elKitBar.innerHTML = `
      <span class="gd-igc__kb-label">${esc(L.kitBar)}</span>
      <span class="gd-igc__kb-pills">${pills}</span>
      ${maxSave > 0 ? `<span class="gd-igc__kb-save">${esc(L.saveUpTo)} <b>${esc(money(maxSave))}</b></span>` : ''}
      <span class="gd-igc__kb-mini"><b>${esc(L.kitBar)}:</b> ${esc(counts)} ${esc(L.trayUnit || 'design')}${
        maxSave > 0 ? ' — ' + esc(String(L.saveUpTo || '').toLowerCase()) + ' ' + esc(money(maxSave)) : ''}</span>
      ${cta}`;
  }

  /* ── chips ──────────────────────────────────────────────────────────── */
  function chipHTML(key, label, swatch) {
    const on = state.chip === key;
    return `<button type="button" class="gd-igc__chip${on ? ' is-on' : ''}" data-chip="${esc(key)}">
      ${swatch ? `<span class="gd-igc__swatch" style="background:${esc(swatch)}"></span>` : ''}${esc(label)}
    </button>`;
  }
  function renderChips() {
    const parts = [chipHTML('all', L.all)];
    if (anyBest && L.chipBest) parts.push(chipHTML('best', L.chipBest));
    if (anyNew && L.chipNew)   parts.push(chipHTML('new', L.chipNew));
    // Colour chips carry a swatch when the theme setting maps that colour family
    // to a hex. Unmapped families still work, just without the dot — better than
    // guessing a colour and printing the wrong one.
    colours.forEach(c => parts.push(chipHTML(c, (SWATCH.label[c] || c.split(' / ')[0]), SWATCH.hex[c])));
    elChips.innerHTML = parts.join('');
  }

  /* ── sort ───────────────────────────────────────────────────────────── */
  function renderSort() {
    if (!elSort) return;
    const cur = SORTS.find(s => s.key === state.sort) || SORTS[0];
    elSort.innerHTML = `
      <button type="button" class="gd-igc__sortbtn" data-sort-toggle aria-expanded="${state.sortOpen}">
        ${esc(L.sortBy)}: ${esc(cur.label)} &#9662;
      </button>
      <div class="gd-igc__sortmenu" ${state.sortOpen ? '' : 'hidden'}>
        ${SORTS.map(s => `<button type="button" class="gd-igc__sortopt${s.key === state.sort ? ' is-on' : ''}"
            data-sort="${s.key}">${esc(s.label)}</button>`).join('')}
      </div>`;
  }

  function renderFilterBtn(n) {
    host.querySelectorAll('[data-igc-filters]').forEach(b => {
      b.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2.2" stroke-linecap="round"><path d="M3 6h18M6 12h12M10 18h4"></path></svg>
        ${esc(L.allFilters)}${n ? `<span class="gd-igc__pip">${n}</span>` : ''}`;
    });
  }

  /* ── scroll lock — the counted protocol every other overlay uses ─────── */
  function lockScroll(el) {
    if (window.gdScrollLock) window.gdScrollLock.lock(el);
    else document.body.style.overflow = 'hidden';
  }
  function unlockScroll(el) {
    if (window.gdScrollLock) window.gdScrollLock.unlock(el);
    else if ((!qv.el || qv.el.hidden) && (!dr.el || dr.el.hidden)) document.body.style.overflow = '';
  }

  /* ── filter drawer ──────────────────────────────────────────────────── */
  const dr = { el: null };
  function ensureDrawer() {
    if (dr.el) return dr.el;
    const n = document.createElement('div');
    n.className = 'gd-igc__dr';
    n.hidden = true;
    n.innerHTML = `<button type="button" class="gd-igc__dr-bd" data-dr-close aria-label="${esc(L.close)}"></button>
      <div class="gd-igc__dr-panel" role="dialog" aria-modal="true" aria-labelledby="gd-igc-dr-h">
        <div class="gd-igc__dr-head"><h2 id="gd-igc-dr-h">${esc(L.allFilters)}</h2>
          <button type="button" class="gd-igc__dr-x" data-dr-close aria-label="${esc(L.close)}">&#10005;</button></div>
        <div class="gd-igc__dr-body" data-dr-body></div>
        <div class="gd-igc__dr-foot">
          <button type="button" class="gd-igc__dr-reset" data-dr-reset>${esc(L.reset)}</button>
          <button type="button" class="gd-igc__dr-cta" data-dr-cta data-dr-close></button>
        </div>
      </div>`;
    document.body.appendChild(n);
    n.addEventListener('click', e => {
      if (e.target.closest('[data-dr-close]')) { closeDrawer(); return; }
      if (e.target.closest('[data-dr-reset]')) { resetAll(); return; }
      const so = e.target.closest('[data-sort]');
      if (so) { state.sort = so.dataset.sort; render(); return; }
      const o = e.target.closest('[data-dr-opt]');
      if (o) {
        const set = state.sets[o.dataset.drGroup], v = o.dataset.drOpt;
        set.has(v) ? set.delete(v) : set.add(v);
        render();
      }
    });
    dr.el = n;
    return n;
  }
  function paintDrawer(shown) {
    if (!dr.el) return;
    // Counts are computed against the CHIP-filtered set, not the whole
    // catalogue. Otherwise "Kort 9" could be shown under an active colour chip
    // and return zero results when tapped.
    const base = DESIGNS.filter(matchChip);
    const countIn = (f, v) => base.reduce((k, d) => k + (f.get(d).indexOf(v) >= 0 ? 1 : 0), 0);
    const body = dr.el.querySelector('[data-dr-body]');
    // paintDrawer replaces the scroll container's innerHTML wholesale, so
    // without this every filter tap threw the shopper back to the top of the
    // panel — worst on phones, where the sort group sits above the facets.
    const keepTop = body.scrollTop;
    // Sort lives in the drawer on phones, where the "Sorter:" pill is hidden to
    // keep the toolbar to one scrolling line. CSS hides this group on desktop.
    const sortGrp = `<div class="gd-igc__dr-sortgrp">
      <div class="gd-igc__dr-t">${esc(L.sortBy)}</div>
      <div class="gd-igc__dr-opts">${SORTS.map(s => `
        <button type="button" class="gd-igc__dr-opt${s.key === state.sort ? ' is-on' : ''}" data-sort="${s.key}">
          <span class="gd-igc__dr-box">${s.key === state.sort ? '&#10003;' : ''}</span>
          <span class="gd-igc__dr-lbl">${esc(s.label)}</span></button>`).join('')}</div></div>`;
    body.innerHTML = sortGrp + FACETS.filter(f => values[f.key].length > 0).map(f => `
      <div class="gd-igc__dr-t">${esc(f.label)}</div>
      <div class="gd-igc__dr-opts">
        ${values[f.key].map(([v]) => {
          const on = state.sets[f.key].has(v);
          const label = f.show ? f.show(v) : v;
          const c = countIn(f, v);
          return `<button type="button" class="gd-igc__dr-opt${on ? ' is-on' : ''}${c === 0 && !on ? ' is-zero' : ''}"
            data-dr-group="${esc(f.key)}" data-dr-opt="${esc(v)}" aria-pressed="${on}">
            <span class="gd-igc__dr-box">${on ? '&#10003;' : ''}</span>
            <span class="gd-igc__dr-lbl">${esc(label)}</span>
            <span class="gd-igc__dr-n">${c}</span></button>`;
        }).join('')}
      </div>`).join('');
    body.scrollTop = keepTop;
    dr.el.querySelector('[data-dr-cta]').textContent =
      tpl(L.drawerCta, 'Vis {n}', { n: shown, unit: L.trayUnit || '' }).trim();
  }
  function openDrawer() {
    ensureDrawer();
    paintDrawer(lastShown);
    dr.el.hidden = false;
    state.sortOpen = false; renderSort();
    lockScroll(dr.el);
    const x = dr.el.querySelector('.gd-igc__dr-x'); if (x) x.focus();
  }
  function closeDrawer() {
    if (!dr.el) return;
    dr.el.hidden = true;
    unlockScroll(dr.el);
    // Return focus to the control that opened it, like closeQv does.
    const btn = host.querySelector('[data-igc-filters]');
    if (btn) btn.focus({ preventScroll: true });
  }
  function resetAll() {
    FACETS.forEach(f => state.sets[f.key].clear());
    state.chip = 'all';
    render();
  }

  /* ── card ───────────────────────────────────────────────────────────── */
  function cardHTML(d) {
    const gal = (d.gallery && d.gallery.length ? d.gallery : [d.img]).filter(Boolean);
    const gi = ((state.imgIdx[d.id] || 0) % gal.length + gal.length) % gal.length;
    const badge = d.isBest && L.bestBadge
      ? `<span class="gd-igc__badge is-best">${esc(L.bestBadge)}</span>`
      : (d.isNew ? `<span class="gd-igc__badge is-new">${esc(L.newBadge)}</span>` : '');
    const nav = gal.length > 1 ? `
      <button type="button" class="gd-igc__nav is-prev" data-nav="-1" data-id="${d.id}" aria-label="&lsaquo;">&lsaquo;</button>
      <button type="button" class="gd-igc__nav is-next" data-nav="1" data-id="${d.id}" aria-label="&rsaquo;">&rsaquo;</button>` : '';
    const dots = gal.length > 1
      ? `<span class="gd-igc__dots">${gal.map((_, i) =>
          `<i class="${i === gi ? 'is-on' : ''}" data-dot="${i}" data-id="${d.id}"></i>`).join('')}</span>`
      : '';
    const meta = [cap(d.len), cap(d.form), (d.design || [])[0]].filter(Boolean).join(' · ');
    const pos = kit.indexOf(d.id);
    const oos = !d.available ? `<span class="gd-igc__oos"><span>${esc(L.soldOut)}</span></span>` : '';
    const add = trayOn && d.available
      ? `<button type="button" class="gd-igc__add${pos >= 0 ? ' is-on' : ''}" data-add="${d.id}"
           title="${esc(pos >= 0 ? L.inKit : L.addToKit)}" aria-label="${esc(pos >= 0 ? L.inKit : L.addToKit)}: ${esc(d.name)}"${busy ? ' disabled' : ''}>${pos >= 0 ? '&#10003;' : '+'}</button>`
      : '';
    return `
      <article class="gd-igc__card${d.available ? '' : ' is-oos'}${pos >= 0 ? ' is-picked' : ''}" data-card="${d.id}">
        <a class="gd-igc__media" href="${esc(d.url)}" data-media="${d.id}" aria-label="${esc(d.name)}">
          <span class="gd-igc__img" style="background-image:url('${esc(gal[gi])}')"></span>
          ${badge}${nav}${dots}${oos}${add}
        </a>
        <div class="gd-igc__body">
          <div class="gd-igc__row">
            <span class="gd-igc__name">${esc(d.name)}</span>
            <span class="gd-igc__price">${esc(money(d.price))}</span>
          </div>
          <div class="gd-igc__meta">${esc(meta)}</div>
        </div>
      </article>`;
  }

  /* ── inserts ─────────────────────────────────────────────────────────
     The preserved "Kombiner & spar" blocks are Liquid-rendered once into a
     hidden holder and MOVED into the grid — never re-serialised — which is what
     guarantees their markup and CSS stay byte-identical to the old grid.

     Because they are MOVED, they must be captured once and detached before
     every grid wipe. The first version read el.firstElementChild on each render:
     the node was moved out on render 1, destroyed by innerHTML on render 2, and
     firstElementChild was null forever after — so both blocks disappeared on the
     shopper's first filter tap. Verified on the draft before this fix. */
  // The canonical story row, Liquid-rendered once and MOVED into the modal —
  // the same technique as the preserved inserts, and for the same reason: the
  // node keeps the listeners gd-stories.js bound to it, so the shared player
  // drives it exactly as it does on the kit page.
  const STORYROW = (() => {
    const src = host.querySelector('[data-igc-storysrc]');
    return src ? src.firstElementChild : null;
  })();

  const INSERTS = elInserts
    ? [...elInserts.querySelectorAll('.gd-igc__insert-src')]
        .map(el => ({ after: parseInt(el.dataset.after, 10) || 0, node: el.firstElementChild }))
        .filter(x => x.node)
        .sort((a, b) => a.after - b.after)
    : [];

  function detachInserts() { INSERTS.forEach(x => { if (x.node.parentNode) x.node.remove(); }); }

  function placeInserts(n) {
    INSERTS.forEach(({ after, node }) => {
      // No `n < after` skip: a shopper who filters to 3 designs used to lose
      // both "Kombiner & spar" blocks entirely. The clamp below appends them
      // after the last card instead, so the merchandising always survives.
      // Re-query per iteration: a static NodeList of PRODUCT CARDS only. Using
      // elGrid.children (live, and counting already-spliced inserts) made every
      // later insert drift earlier by the number of inserts before it.
      const cards = elGrid.querySelectorAll('.gd-igc__card');
      const at = Math.min(Math.max(after, 0), cards.length);
      elGrid.insertBefore(node, cards[at] || null);
    });
  }

  let lastShown = DESIGNS.length;

  function render() {
    const list = sorted(DESIGNS.filter(d => matchChip(d) && matchFacets(d)));
    lastShown = list.length;
    renderKitBar();
    renderChips();
    renderSort();
    renderFilterBtn(activeCount());
    if (elCount) elCount.textContent = tpl(L.results, '{n} / {total}', { n: list.length, total: DESIGNS.length });
    if (dr.el && !dr.el.hidden) paintDrawer(list.length);

    detachInserts();                       // ALWAYS before a wipe, including the
                                           // zero-results path below.
    if (!list.length) {
      elGrid.innerHTML = '';
      elEmpty.innerHTML = `<div class="gd-igc__empty-t">${esc(L.emptyTitle)}</div>
        <div class="gd-igc__empty-s">${esc(L.emptySub)}</div>
        <button type="button" class="gd-igc__reset" data-reset>${esc(L.resetFilters)}</button>`;
      elEmpty.hidden = false;
      renderTray();
      return;
    }
    elEmpty.hidden = true;
    elGrid.innerHTML = list.map(cardHTML).join('');
    placeInserts(list.length);
    renderTray();
    // Never under a mounted or fading upsell: offerUpsell closes the window when it settles.
    if (qv.el && !qv.el.hidden && !qv.el.querySelector('.gd-upsell')) paintQv();   // keep the open modal's button in sync
  }

  /* ── quick view ────────────────────────────────────────────────────── */
  const qv = { el: null, id: null, gi: 0, seq: 0 };   // seq: bumped on every close

  function ensureQv() {
    if (qv.el) return qv.el;
    const n = document.createElement('div');
    n.className = 'gd-igc__qv';
    n.setAttribute('data-igc-qv', '');
    n.hidden = true;
    n.innerHTML = '<button type="button" class="gd-igc__qv-bd" data-qv-close aria-label="' +
      esc(L.close) + '"></button><div class="gd-igc__qv-card" role="dialog" aria-modal="true" aria-label="' +
      esc(L.close) + '"></div>';
    document.body.appendChild(n);
    n.addEventListener('click', e => {
      if (e.target.closest('[data-qv-close]')) { closeQv(); return; }
      const nav = e.target.closest('[data-qv-nav]');
      if (nav) { qvGo(qv.gi + +nav.dataset.qvNav); return; }
      const th = e.target.closest('[data-qv-to]');
      if (th) { qvGo(+th.dataset.qvTo); return; }
      const b = e.target.closest('[data-qv-buy]');
      if (b) {
        // Enter/Space still fire click on an aria-busy button (the CSS gate only stops
        // pointers) and gd-cart-add.js keeps no in-flight flag. Cancelling the click
        // cancels the submit, so a held Enter cannot buy the same set twice.
        if (b.getAttribute('aria-busy') === 'true' || b.classList.contains('is-loading')) { e.preventDefault(); return; }
        emit('instant_gels_qv_buy_click', { handle: (byId(+b.dataset.qvBuy) || {}).handle });
        return;
      }
      const a = e.target.closest('[data-qv-add]');
      // Close on a successful pick — the same thing the kit page's detail view
      // does (instant-gels-builder.js:539, the "salon pattern"). Two reasons
      // beyond parity. On a phone this quick view is a full-screen sheet that
      // completely covers the tray, so a shopper who taps "add to kit" sees no
      // confirmation at all except the button's own label flipping; closing is
      // what reveals the slot filling and the rail advancing. And the job on
      // this page is to pick SEVERAL designs, so staying open costs a mandatory
      // extra tap on every one of them — four taps for the 4-kit, eight for the 8.
      //
      // toggleKit returns false when the pick was refused (design unavailable,
      // or the tray is mid-request). Stay open then, so the reason stays on
      // screen — the kit page keeps its detail open for an out-of-stock design
      // for exactly the same reason.
      if (a) { if (toggleKit(+a.dataset.qvAdd)) closeQv(); }
    });
    // Desktop mouse drag on the photo strip — gd-drag-scroll.js rules: mouse only, 5px threshold,
    // pointer capture only once it is a real drag, no preventDefault on pointerdown, the click right
    // after a drag is swallowed. Release: past 15% of the width -> next/previous photo, else back.
    n.addEventListener('pointerdown', e => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button')) return;
      const t = e.target.closest('[data-qv-track]');
      if (!t || t.children.length < 2) return;
      qvDrag = { t, id: e.pointerId, x: e.clientX, last: e.clientX, left: t.scrollLeft, moved: false, raf: 0 };
    });
    document.addEventListener('pointermove', e => {
      const g = qvDrag;
      if (!g || e.pointerId !== g.id) return;
      g.last = e.clientX;
      if (!g.moved) {
        if (Math.abs(g.last - g.x) <= 5) return;
        g.moved = true;
        try { g.t.setPointerCapture(g.id); } catch (_) {}
        g.t.classList.add('is-dragging');
      }
      e.preventDefault();
      if (!g.raf) g.raf = requestAnimationFrame(() => { g.raf = 0; g.t.scrollLeft = g.left - (g.last - g.x); });
    });
    const qvDragEnd = e => {
      const g = qvDrag;
      if (!g || e.pointerId !== g.id) return;
      qvDrag = null;
      if (!g.moved) return;
      try { g.t.releasePointerCapture(g.id); } catch (_) {}
      const w = g.t.clientWidth || 1, dx = g.last - g.x, from = Math.round(g.left / w);
      const k = Math.max(0, Math.min(g.t.children.length - 1, Math.abs(dx) > w * 0.15 ? from + (dx < 0 ? 1 : -1) : from));
      qvGo(k);
      setTimeout(() => g.t.classList.remove('is-dragging'), 450);
      const up = e.timeStamp, swallow = ev => { if (ev.timeStamp - up <= 80) { ev.stopPropagation(); ev.preventDefault(); } };
      g.t.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => g.t.removeEventListener('click', swallow, true), 80);
    };
    document.addEventListener('pointerup', qvDragEnd);
    document.addEventListener('pointercancel', qvDragEnd);
    qv.el = n;
    return n;
  }

  // (The bespoke story viewer that used to live here is gone — "Se og lær" now
  //  uses the shared gd-stories.js player via STORYROW, the same one the kit
  //  page and the Instant Gel PDP use.)

  const galOf = d => (d.gallery && d.gallery.length ? d.gallery : [d.img]).filter(Boolean);

  // ── quick-view photos: photo strip (salon kit pattern) ─────────────────
  // Claude 2026-10-07 (owner request): one background swapped by the arrows could not follow a finger or
  // a mouse. Every photo is now a slide in a horizontal scroll-snap strip, like the salon kit builder:
  // touch scrolls it natively, a desktop mouse drags it (see ensureQv), arrows/thumbs glide it and the
  // counter + thumbs follow the scroll. qv.gi stays the index, so a re-paint lands on the same photo.
  let qvDrag = null;
  function qvTrack() { return qv.el && qv.el.querySelector('[data-qv-track]'); }
  function qvSync(i) {
    const t = qvTrack(), n = t ? t.children.length : 0;
    if (!n) return;
    qv.gi = ((i % n) + n) % n;
    const c = qv.el.querySelector('.gd-igc__qv-count');
    if (c) c.textContent = `${qv.gi + 1} / ${n}`;
    qv.el.querySelectorAll('.gd-igc__qv-thumb').forEach((b, k) => b.classList.toggle('is-on', k === qv.gi));
  }
  function qvGo(i) {
    const t = qvTrack(), n = t ? t.children.length : 0;
    if (!n) return;
    qvSync(i);
    t.scrollTo({ left: qv.gi * t.clientWidth, behavior: 'smooth' });
  }
  function bindQvTrack(gi) {
    const t = qvTrack();
    if (!t) return;
    t.scrollLeft = gi * t.clientWidth;            // same photo after a re-paint, no animation
    let raf = 0;
    t.addEventListener('scroll', () => {
      if (raf || qvDrag) return;                  // during a mouse drag the release decides
      raf = requestAnimationFrame(() => {
        raf = 0;
        const w = t.clientWidth, k = w ? Math.round(t.scrollLeft / w) : 0;
        if (w && k !== qv.gi) qvSync(k);
      });
    }, { passive: true });
  }

  function paintQv() {
    if (qv.el && qv.el.querySelector('.gd-upsell:not(.gd-upsell--leaving)')) return;
    const d = DESIGNS.find(x => x.id === qv.id);
    if (!d) return;
    const gal = galOf(d);
    const gi = ((qv.gi % gal.length) + gal.length) % gal.length;
    qv.gi = gi;

    const badge = d.isBest && L.bestBadge
      ? `<span class="gd-igc__badge is-best">${esc(L.bestBadge)}</span>`
      : (d.isNew ? `<span class="gd-igc__badge is-new">${esc(L.newBadge)}</span>` : '');

    // Row order is the handoff's: Lengde | Dekkevne, then Form | Design, then a
    // wide row. The mock's wide row is "Finish", which has no metafield in this
    // catalogue (only length_label, shape, coverage, design, colour_family), so
    // Farge takes that slot rather than inventing a field.
    const designs = (d.design || []).join(' · ');
    const pairs = [
      [L.specLen, cap(d.len)], [L.specCover, cap(d.cover)],
      [L.specForm, cap(d.form)], [L.specDesign, designs],
    ].filter(r => r[1]);
    const spec = pairs.map(r => `<dt>${esc(r[0])}</dt><dd>${esc(r[1])}</dd>`).join('') +
      (d.colour ? `<dt>${esc(L.specColour)}</dt><dd class="is-wide">${esc(cap(d.colour))}</dd>` : '');

    const pills = (QV.pills || []).length
      ? `<div class="gd-igc__qv-pills">${QV.pills.map(p => `<span>${esc(p)}</span>`).join('')}</div>` : '';
    const incl = (QV.included || []).length ? `
      <div class="gd-igc__qv-kicker">${esc(QV.includedTitle)}</div>
      <div class="gd-igc__qv-incl">${QV.included.map(i => `
        <div>${i.img ? `<img src="${esc(i.img)}" alt="" loading="lazy">` : ''}
          <span>${esc(i.line1)}${i.line2 ? '<br>' + esc(i.line2) : ''}</span></div>`).join('')}</div>` : '';
    // "Se og lær" is an empty slot; the Liquid-rendered story row is moved in
    // after paint. No bespoke viewer here any more — the shared player owns it.
    const learn = STORYROW ? `
      <div class="gd-igc__qv-kicker">${esc(QV.learnTitle)}</div>
      <div class="gd-igc__qv-learn" data-qv-storyslot></div>` : '';

    // PRIMARY: buy this one set, straight to the cart, through the audited
    // <form action="/cart/add" data-gd-quick-add> path that gd-cart-add.js
    // intercepts — the same path as the product page, so the toast, Klaviyo, the
    // L1 guard and both gift engines apply. Property-free on purpose: a single
    // must stay a plain retail line (cart contract at the top of the tray code).
    // The price is in the label TEXT, not a child span: gd-cart-add.js restores
    // the button with textContent after the request, which flattens markup, and
    // the phone layout hides .gd-igc__qv-add-p.
    // SECONDARY: the kit tray, unchanged (toggleKit). The kit is an option, not a
    // gate — the two used to be one button that only ever staged into the tray.
    const inKit = kit.indexOf(d.id) >= 0;
    const buy = !d.available
      ? `<div class="gd-igc__qv-add is-off">${esc(L.soldOut)}</div>`
      : (d.variantId > 0
        ? `<form method="post" action="/cart/add" class="gd-igc__qv-buy" data-gd-quick-add data-igc-buy="${d.id}">
            <input type="hidden" name="id" value="${d.variantId}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="gd-igc__qv-add gd-async-btn" data-qv-buy="${d.id}"${busy ? ' disabled' : ''}><span class="gd-async-btn__label">${esc(tpl(L.buyOne, 'Kj\u00f8p 1 sett \u2014 {price}', { price: money(d.price) }))}</span></button>
            <p class="gd-igc__qv-err" data-gd-cart-form-error hidden></p>
          </form>`
        : '');
    const kitAdd = trayOn && d.available
      ? `<button type="button" class="gd-igc__qv-kit${inKit ? ' is-in' : ''}" data-qv-add="${d.id}"${busy ? ' disabled' : ''}>${esc(inKit
          ? L.inKit
          : (canBundle(d)
            ? tpl(L.qvKitAdd, '+ Legg i kit \u00b7 {n} for {price}',
                { n: TIERS[0].count, price: money(TIERS[0].price), unit: L.trayUnit || '' })
            : (L.addToKit || '+')))}</button>`
      : '';

    const trust = (QV.trust || []).length ? `
      <div class="gd-igc__qv-trust">${QV.trust.map((t, i) => `
        <div>${i === 0 && (QV.faces || []).length
          ? // NOT lazy: three 24px avatars that live at the bottom of a scrollable
          // overlay. Lazy-loading never fired for them — the trust row sits below
          // the modal's fold, so the images stayed `complete:false` forever.
          `<span class="gd-igc__qv-faces">${QV.faces.map(f => `<img src="${esc(f)}" alt="" width="24" height="24">`).join('')}</span>`
          : ICONS[i] || ''}<span>${esc(t)}</span></div>`).join('')}</div>` : '';

    const oldBuy = qv.el.querySelector('form[data-igc-buy]');
    const keepBuy = oldBuy && +oldBuy.getAttribute('data-igc-buy') === d.id
      && oldBuy.querySelector('[aria-busy="true"]') ? oldBuy : null;
    qv.el.querySelector('.gd-igc__qv-card').innerHTML = `
      <div class="gd-igc__qv-top">
        <button type="button" class="gd-igc__gh" data-qv-close aria-label="${esc(L.close)}">&lsaquo;</button>
        <div class="gd-igc__qv-title"><h2>${esc(d.name)}</h2>${badge}</div>
        <button type="button" class="gd-igc__gh is-x" data-qv-close aria-label="${esc(L.close)}">&#10005;</button>
      </div>
      <div class="gd-igc__qv-main">
        <div class="gd-igc__qv-left">
          <div class="gd-igc__qv-gal">
            <button type="button" class="gd-igc__qv-xfloat" data-qv-close
              aria-label="${esc(L.close)}">&#10005;</button>
            <div class="gd-igc__qv-track" data-qv-track>${gal.map((u, i) => `<span class="gd-igc__qv-slide" style="background-image:url('${esc(u)}')" role="img" aria-label="${esc(d.name)} ${i + 1} / ${gal.length}"></span>`).join('')}</div>
            ${gal.length > 1 ? `
              <button type="button" class="gd-igc__qv-nav is-prev" data-qv-nav="-1" aria-label="&lsaquo;">&lsaquo;</button>
              <button type="button" class="gd-igc__qv-nav is-next" data-qv-nav="1" aria-label="&rsaquo;">&rsaquo;</button>
              <span class="gd-igc__qv-count">${gi + 1} / ${gal.length}</span>` : ''}
          </div>
          ${gal.length > 1 ? `<div class="gd-igc__qv-thumbs">${gal.map((u, i) =>
            `<button type="button" class="gd-igc__qv-thumb${i === gi ? ' is-on' : ''}" data-qv-to="${i}"
               style="background-image:url('${esc(u)}')" aria-label="${i + 1}"></button>`).join('')}</div>` : ''}
        </div>
        <div class="gd-igc__qv-right">
          ${`<div class="gd-igc__qv-mtitle"><h3>${esc(d.name)}</h3>${badge}</div>`}
          <div class="gd-igc__qv-priced">
            <span class="gd-igc__qv-price">${esc(money(d.price))}</span>
            ${QV.fromNote ? `<span class="gd-igc__qv-from">${esc(QV.fromNote)}</span>` : ''}
          </div>
          ${(d.desc || QV.desc) ? `<div class="gd-igc__qv-desc">${esc(d.desc || QV.desc)}</div>` : ''}
          <dl class="gd-igc__qv-spec">${spec}</dl>
          ${QV.mobileSummary ? `<div class="gd-igc__qv-sum">${esc(QV.mobileSummary)}</div>` : ''}
          ${pills}${incl}${learn}
          <div class="gd-igc__qv-foot">
            <div class="gd-igc__qv-actions">
              ${buy}
              <button type="button" class="gd-igc__qv-lukk" data-qv-close>${esc(L.closeLabel)}</button>
            </div>
            ${kitAdd}
            <a class="gd-igc__qv-pdp" href="${esc(d.url)}">
              <b>${esc(L.seeFull)}</b>${QV.pdpNote ? `<i>${esc(QV.pdpNote)}</i>` : ''}</a>
            <div class="gd-igc__qv-mlinks">
              <a href="${esc(d.url)}">${esc(L.seeFull)}</a>
              ${kitAdd ? '' : kitLink()}
            </div>
            ${L.keepBrowsing ? `<div class="gd-igc__qv-cont">
              <button type="button" data-qv-close>${esc(L.keepBrowsing)}</button>
              ${QV.guarantee ? `<em></em><span>${esc(QV.guarantee)}</span>` : ''}</div>` : ''}
          </div>
        </div>
      </div>
      ${trust}`;
    if (keepBuy) { const nb = qv.el.querySelector('form[data-igc-buy]'); if (nb) nb.replaceWith(keepBuy); }
    // Re-home the row: paintQv replaces the card's innerHTML, which would
    // otherwise orphan it. Moving preserves the player's listeners.
    if (STORYROW) {
      const slot = qv.el.querySelector('[data-qv-storyslot]');
      if (slot) slot.appendChild(STORYROW);
    }
    bindQvTrack(gi);
  }

  function kitLink() {
    if (!TIERS.length) return '';
    const t = TIERS[0];
    const label = tpl(L.qvKitLink, 'Bygg kit \u2014 {n} for {price}',
      { n: t.count, price: money(t.price), unit: L.trayUnit || '' });
    const href = L.kitUrl || insertHref();
    return `<em></em>` + (href
      ? `<a href="${esc(href)}">${esc(label)}</a>`
      : `<span>${esc(label)}</span>`);
  }

  const ICONS = [
    '',
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7A4EC6" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l7 3v6c0 4.8-3.2 8.4-7 10-3.8-1.6-7-5.2-7-10V5l7-3z"></path><path d="M9 12l2 2 4-4"></path></svg>',
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7A4EC6" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M1 7h12v9H1z"></path><path d="M13 10h4l3 3v3h-7"></path><circle cx="5.5" cy="18" r="1.6"></circle><circle cx="16.5" cy="18" r="1.6"></circle></svg>',
  ];

  let storiesWarmed = false;
  function warmStories() {
    if (storiesWarmed) return;
    storiesWarmed = true;
    (QV.learn || []).forEach(i => { if (i.full) { const im = new Image(); im.src = i.full; } });
  }

  function openQv(id) {
    ensureQv();
    warmStories();
    qv.id = id; qv.gi = 0;
    paintQv();
    qv.el.hidden = false;
    qv.el.scrollTop = 0;
    lockScroll(qv.el);
    // Below 900px the header bar is display:none, so .gd-igc__gh cannot take
    // focus — fall back to the floating ✕ that replaces it.
    const x = [...qv.el.querySelectorAll('.gd-igc__gh, .gd-igc__qv-xfloat')]
      .find(b => b.offsetParent !== null);
    if (x) x.focus();
    emit('instant_gels_quickview_open', { handle: (byId(id) || {}).handle });
  }
  // Salon-kit style upsell after a single-set buy (gd-upsell-modal.js). The
  // module mounts INSIDE the quick-view card and resolves once the shopper has
  // accepted, declined or closed. While it is showing, the confirmation toast
  // sits under the quick view; so, exactly like the salon kit, the cart drawer
  // opens once the offer is done. If it is skipped (already declined this
  // session, both products already in the cart, section absent) the quick view
  // just closes and the toast shows as before.
  async function dropPaidDupes(extras) {
    const free = new Map();
    (extras || []).forEach(x => { if (x && x.sku) free.set(x.sku, (free.get(x.sku) || 0) + Math.max(1, parseInt(x.qty, 10) || 1)); });
    if (!free.size || ![...free.keys()].some(k => paidUpsellSkus.has(k))) return;
    try {
      const c = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const updates = {}, dropped = [];
      (c.items || []).forEach(l => {
        const p = l.properties || {};
        const n = p._upsell === 'true' && l.sku ? (free.get(l.sku) || 0) : 0;
        if (n > 0) {
          const take = Math.min(n, l.quantity);
          updates[l.key] = l.quantity - take;
          free.set(l.sku, n - take);
          dropped.push(l.sku);
        }
      });
      if (!dropped.length) return;
      const u = await fetch('/cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (u.ok) emit('instant_gels_upsell_dupe_removed', { skus: dropped });
    } catch (e) { console.warn('[gd-igc] paid duplicate not removed', e); }
  }

  function offerUpsell(d) {
    const up = window.gdUpsell;
    const card = qv.el && !qv.el.hidden ? qv.el.querySelector('.gd-igc__qv-card') : null;
    if (!up || typeof up.maybeShow !== 'function' || !card) {
      if (qv.el && !qv.el.hidden) closeQv();
      return;
    }
    // The offer belongs to THIS opening of the window. closeQv bumps qv.seq, so
    // if the shopper closes (or closes and reopens) while the cart is being
    // read, the offer is never mounted into a hidden or different window.
    const openId = qv.id, seq = qv.seq;
    const isCurrent = () => !!qv.el && !qv.el.hidden && qv.seq === seq && qv.id === openId;
    Promise.resolve()
      .then(() => isCurrent()
        ? up.maybeShow({ source: 'instant_gels_single', handle: d.handle, price_cents: d.price },
                       { host: card, context: 'instant_gels_single', isCurrent })
        : { accepted: false, shown: false, outcome: 'skipped' })
      .catch(() => ({}))
      .then(r => {
        if (!mine()) return;
        // Only close the window this offer belongs to.
        if (isCurrent()) closeQv();
        // Accept, or "Tacka nej · Visa varukorg": show the cart, like the salon kit.
        // Closed with ✕ / Escape / backdrop: just close.
        if (r && r.shown && (r.outcome === 'accepted' || r.outcome === 'declined')) {
          document.body.dispatchEvent(new CustomEvent('gd:cart-open'));
        }
      });
  }

  function closeQv() {
    if (!qv.el) return;
    // Closing the window (backdrop, Escape) while the upsell is up must settle
    // it, or its promise and key listener would be left hanging.
    const up = qv.el.querySelector('.gd-upsell:not(.gd-upsell--leaving)');
    if (up) up.dispatchEvent(new CustomEvent('gd:upsell:close', { bubbles: true }));
    qv.seq++;
    qv.el.hidden = true;
    unlockScroll(qv.el);
    const card = document.querySelector(`[data-card="${qv.id}"] .gd-igc__media`);
    if (card) card.focus({ preventScroll: true });
    qv.id = null;
  }
  document.addEventListener('keydown', e => {
    if (!mine() || e.key !== 'Escape') return;
    if (qv.el && !qv.el.hidden) { closeQv(); return; }
    if (dr.el && !dr.el.hidden) { closeDrawer(); return; }
    if (state.sortOpen) { state.sortOpen = false; renderSort(); }
  });
  // Close on a successful add as well as gd:cart-open. When the in-place
  // confirmation toast owns an add it deliberately suppresses gd:cart-open, so
  // listening for that alone left quick view sitting open behind the toast.
  //
  // But `product-added-to-cart` is NOT only fired by shopper actions: both gift
  // reconcilers dispatch it whenever they drop a free line in (gwp.js ~L288,
  // tier-gifts.js ~L305), unprompted, including on page load and after a late
  // collection fetch. Closing on those yanked the modal shut while the shopper
  // was still reading it. Only close for an add this tray actually made.
  let ownAdd = false;
  document.body.addEventListener('product-added-to-cart', e => {
    if (!mine()) return;
    const f = e && e.detail && e.detail.form;
    if (f && f.hasAttribute && f.hasAttribute('data-igc-buy')) {
      const id = +f.getAttribute('data-igc-buy');
      // A design bought on its own must not stay in the tray, or the tray CTA
      // would sell it a second time. offer() still counts it as owned in the cart.
      const i = kit.indexOf(id);
      if (i >= 0) { kit.splice(i, 1); saveKit(); render(); }
      const d = byId(id) || {};
      emit('instant_gels_qv_buy_success', { handle: d.handle, price: d.price });
      // A single set comes without Remove or Heal — offer them the way the salon
      // kit does, inside this same window. Falls back to a plain close.
      offerUpsell(d);
      return;
    }
    if (ownAdd && qv.el && !qv.el.hidden) closeQv();
  });
  // gd:cart-open stays unconditional — whatever opened the drawer is about to
  // cover the modal, so leaving it open underneath helps nobody.
  document.body.addEventListener('gd:cart-open', () => { if (mine() && qv.el && !qv.el.hidden) closeQv(); });

  /* ── tray render + submit ───────────────────────────────────────────── */
  let flashTimer = 0;
  function flash(msg) {
    const el = elTray && elTray.querySelector('[data-tray-msg]');
    if (!el) return;
    clearTimeout(flashTimer);
    el.textContent = msg; el.hidden = false;
    flashTimer = setTimeout(() => { el.hidden = true; }, 3200);
  }

  // Milestones come from the REAL tiers: the discount percentage is derived
  // (1 - tierPrice / (count x unitPrice)), the sub-label is the tier's own free
  // extras, and the position is count/maxCount. Nothing here is hardcoded, so
  // the rail stays honest on all four markets and after any price change.
  function milestones() {
    if (!TIERS.length) return [];
    const unit = unitPrice();
    const max = TIERS[TIERS.length - 1].count;
    // A milestone box must never be wider than the space between two
    // milestones, and that space is a PERCENTAGE of the rail — so a fixed px
    // width only works at the width it was measured at. At 1280 the rail is
    // 1099px and 150px boxes sit 125px apart; at 760 the rail is 594px, the
    // spacing drops to 148px and the same boxes collide. Deriving the width
    // from the smallest adjacent gap makes it correct at every width, and for
    // any tier configuration a store might use.
    const pos = TIERS.map(t => (t.count / max) * 100);
    let minGap = 100;
    for (let i = 1; i < pos.length; i++) minGap = Math.min(minGap, pos[i] - pos[i - 1]);
    if (!isFinite(minGap) || minGap <= 0) minGap = 100;
    return TIERS.map((t, i) => {
      const full = t.count * unit;
      const pct = full > 0 ? Math.round((1 - t.price / full) * 100) : 0;
      return {
        count: t.count,
        pct: pct > 0 ? '\u2013' + pct + '%' : '',
        label: t.count + ' ' + (L.trayUnit || 'design'),
        // The merchant-set short label wins: the derived one is built from full
        // product titles ("Remove Solution + Heal") which cannot fit the ~66px
        // a phone milestone gets. Falls back to the derived text when unset.
        sub: t.railLabel || t.extras.map(x => x.title).filter(Boolean).join(' + '),
        pos: pos[i],
        // percentage of the rail, minus a fixed gutter so neighbours never touch
        width: 'calc(' + minGap.toFixed(3) + '% - 12px)',
      };
    });
  }

  function renderTray() {
    if (!trayOn) return;
    const n = kit.length;
    const off = offer();
    // The tray now has a second reason to exist: a shopper who added designs
    // one at a time has nothing picked, but is the one person on the page who
    // most needs to hear that the same designs are cheaper as a kit.
    if (!n && !off) { elTray.hidden = true; elTray.innerHTML = ''; elTray.classList.remove('is-below-tier'); return; }

    const offerVars = {
      n: off ? off.chosen.length : 0,
      owned: off ? off.fromCart : 0,
      picked: off ? off.fromTray : 0,
      price: off ? money(off.tier.price) : '',
      was: off ? money(off.retail) : '',
      save: off ? money(off.save) : '',
      extras: off ? off.tier.extras.map(x => x.title).filter(Boolean).join(' + ') : '',
    };
    const offerRow = off ? `
      <div class="gd-igc__tray-offer">
        <span class="gd-igc__tray-offer-t">${esc(off.fromTray > 0
          ? tpl(L.offerMsgMixed, '{owned} i handlekurven + {picked} valgt — som kit: {price} i stedet for {was}', offerVars)
          : tpl(L.offerMsg,      'Du har {n} i handlekurven — som kit: {price} i stedet for {was}', offerVars))}</span>
        <button type="button" class="gd-igc__tray-offer-cta" data-tray-convert ${busy ? 'disabled' : ''}>${
          esc(busy ? (L.trayAdding || '…') : tpl(L.offerCta, 'Bytt til kit · spar {save}', offerVars))
        }</button>
      </div>` : '';

    // Nothing picked: the offer is the whole tray. No slots, no total, no
    // add-to-bag — quoting a price for an empty selection would be a lie.
    if (!n) {
      elTray.hidden = false;
      elTray.classList.remove('is-below-tier');
      elTray.innerHTML = offerRow + `<p class="gd-igc__tray-err" data-tray-msg hidden></p>`;
      return;
    }
    const p = plan();
    const defer = !!(off && off.fromTray > 0);
    const strike = kitStrike();
    const save = strike - p.total;
    const max = TIERS[TIERS.length - 1].count;
    // "next" is measured against designs that can ACTUALLY fill slots, so the
    // tray never promises a tier the payload cannot build.
    const next = TIERS.find(x => x.count > p.bundleable);
    const need = next ? next.count - p.bundleable : 0;

    // Slot chips: one per slot of the tier being worked toward, but NEVER fewer
    // than the number of designs actually picked. `next` is measured against
    // BUNDLEABLE designs, so it lags `n` whenever a pick has no available $0
    // component — pick 5 with 2 unbundleable and next.count is 4, which rendered
    // 4 chips for 5 picks: the 5th design was counted in the title and charged in
    // the total but had no thumbnail and no ✕ to remove it. Latent today (all 32
    // components are in stock on all four stores) and live the moment one sells out.
    // Below the smallest tier: only the filled chips (no empty numbered 2, 3, 4
    // that read as "you must fill these"), and the "loose" title. The empty
    // slots return once a kit tier is reached, where building the kit is the point.
    const below = !p.tier;
    const slotCount = below ? n : Math.max(n, next ? next.count : max);
    const slots = Array.from({ length: slotCount }, (_, i) => {
      const id = kit[i];
      const d = id === undefined ? null : byId(id);
      if (!d) return `<span class="gd-igc__slot">${i + 1}</span>`;
      const img = (d.gallery && d.gallery[0]) || d.img;
      // A pick with no usable $0 component is in the cart but cannot fill a
      // bundle slot, so it must not read as one against an unlit rail.
      const solo = canBundle(d) ? '' : ' is-solo';
      return `<span class="gd-igc__slot is-filled${solo}" data-slot-open="${d.id}" title="${esc(d.name)}">
          <span class="gd-igc__slot-img" style="background-image:url('${esc(img)}')"></span>
          <button type="button" class="gd-igc__slot-x" data-slot-remove="${d.id}"
            aria-label="${esc(L.trayClear)} ${esc(d.name)}">&#10005;</button>
        </span>`;
    }).join('');

    // Three states, because "Du sparer 0 kr" below the first tier reads as a
    // broken string rather than an invitation.
    // {extras} = what the NEXT tier gives away. "3 til gir kit-pris 1 099 kr
    // + gratis Heal" is a stronger line than the discount alone, and it keeps
    // the promise readable on phones where the rail is tight.
    const nextExtras = next
      ? (next.railLabel || next.extras.map(x => x.title).filter(Boolean).join(' + '))
      : '';
    const vars = { n: need, price: next ? money(next.price) : '', save: money(save), extras: nextExtras };
    const msg = need > 0
      ? (save > 0
          ? tpl(L.trayMsgNext,  'Du sparer {save}! {n} til gir {price}', vars)
          : tpl(L.trayMsgStart, 'Legg til {n} til for kit-pris {price}', vars))
      : tpl(L.trayMsgMax, 'Maks rabatt', vars);

    const rail = TIERS.length ? `
      <div class="gd-igc__rail">
        <div class="gd-igc__rail-bg"><div class="gd-igc__rail-fill"
          style="width:${Math.min(p.bundleable / max, 1) * 100}%"></div></div>
        ${milestones().map(m => `
          <div class="gd-igc__ms${p.bundleable >= m.count ? ' is-on' : ''}" style="left:${m.pos}%;width:${m.width}">
            <span class="gd-igc__ms-c">${esc(m.pct)}</span>
            <span class="gd-igc__ms-l">${esc(m.label)}</span>
            ${m.sub ? `<span class="gd-igc__ms-s">+ ${esc(m.sub)}</span>` : ''}
          </div>`).join('')}
      </div>` : '';

    // Desktop is one flex row then the rail. The phone layout from the handoff is
    // a different ARRANGEMENT of the same parts: title + total, rail, message,
    // then slots + CTA. Both are expressed from this one DOM by letting the
    // wrappers become `display:contents` on mobile, so their children promote to
    // grid items and can be placed by area.
    elTray.hidden = false;
    elTray.classList.toggle('is-below-tier', below);
    elTray.innerHTML = offerRow + `
      <div class="gd-igc__tray-main">
        <span class="gd-igc__slots">${slots}</span>
        <span class="gd-igc__tray-copy">
          <span class="gd-igc__tray-title">${esc(tpl(below && L.trayTitleLoose ? L.trayTitleLoose : L.trayTitle, '{n} design', { n }))}</span>
          ${defer ? '' : `<span class="gd-igc__tray-msg">\u2726 ${esc(msg)}</span>`}
        </span>
        ${defer ? '' : `<span class="gd-igc__tray-money">
          <b>${esc(money(p.total))}</b>
          ${save > 0 ? `<s>${esc(money(strike))}</s>` : ''}
        </span>`}
        <button type="button" class="gd-igc__tray-clear" data-tray-clear ${busy ? 'disabled' : ''}>${esc(L.trayClear || 'T\u00f8m')}</button>
        ${defer ? '' : `<button type="button" class="gd-igc__tray-cta" data-tray-cta ${busy ? 'disabled' : ''}>
          ${busy ? esc(L.trayAdding) : esc(L.trayCta)}<span class="gd-igc__tray-cta-p"> \u2014 ${esc(money(p.total))}</span>
        </button>`}
      </div>
      ${defer ? '' : rail}
      <p class="gd-igc__tray-err" data-tray-msg hidden></p>`;
  }

  function buildPayload(p) {
    if (!p.tier) {
      // Below the smallest tier — plain singles, no bundle, no properties.
      return p.overflow.map(id => ({ id: byId(id).variantId, quantity: 1 }));
    }
    const bundleId = 'igc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    const names = p.chosen.map(id => byId(id).name).join(',');
    return [
      { id: p.tier.variantId, quantity: 1, properties: { Designs: names, __bundle_id: bundleId, _kit: 'true' } },
      ...p.chosen.map(id => ({ id: byId(id).componentVariantId, quantity: 1,
                               properties: { __bundle_id: bundleId, _addon: 'true' } })),
      ...p.tier.extras.filter(x => x && x.id).map(x => ({ id: x.id, quantity: Math.max(1, parseInt(x.qty, 10) || 1),
                               properties: { __bundle_id: bundleId, _addon: 'true' } })),
      // overflow: RETAIL variants, deliberately property-free
      ...p.overflow.map(id => ({ id: byId(id).variantId, quantity: 1 })),
    ];
  }

  async function submitTray() {
    if (busy || !kit.length) return;
    const p = plan();                       // ONE partition: priced and sent
    const items = buildPayload(p);
    const submitted = p.chosen.concat(p.overflow);
    busy = true; render();
    emit('instant_gels_tray_atc_click', {
      tier_id: p.tier ? p.tier.variantId : null, design_count: kit.length, price: p.total,
    });
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });
      // Read the body ONCE, guarded, and keep it: the confirmation dispatch below
      // needs it as `detail.line` so the toast can name the product. An earlier
      // revision only parsed it inside the !r.ok branch, so the success path
      // referenced an undeclared `body` — the POST succeeded, the ReferenceError
      // was swallowed by the catch, and the shopper got NO confirmation at all.
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.description || body.message || L.cartError);
      emit('instant_gels_tray_atc_success', { tier_id: p.tier ? p.tier.variantId : null, price: p.total });
      if (p.tier) await dropPaidDupes(p.tier.extras);
      try {
        if (window._learnq && typeof window._learnq.push === 'function') {
          window._learnq.push(['track', 'Added to Cart', {
            $value: (p.total || 0) / 100,
            AddedItemProductName: p.tier ? (p.tier.title || 'Instant Gels Bundle') : 'Instant Gels',
            AddedItemQuantity: submitted.length,
            BundleDesigns: p.chosen.map(id => byId(id).name),
            BundleSlotCount: p.tier ? p.tier.count : 0,
          }]);
        }
      } catch (e) {}
      // Clear BEFORE the events fire — the kit builder shipped without this and
      // a second tap silently bought a second bundle. Remove only what was
      // actually SENT, so a design added while the request was in flight is not
      // thrown away along with it.
      submitted.forEach(id => { const i = kit.indexOf(id); if (i >= 0) kit.splice(i, 1); });
      saveKit();
      // CONFIRMATION PARITY WITH EVERY OTHER COLLECTION PAGE.
      // Previously this dispatched a bare `product-added-to-cart` plus an
      // unconditional `gd:cart-open`, which took the screen over with the cart
      // drawer — while the colour collections confirm in place with the toast.
      // gd-cart-drawer.js asks `window.gdCartToast.handles(detail)`, and the
      // toast answers true when `detail.form` carries [data-gd-quick-add]; the
      // drawer then shows the toast itself and does NOT open. So we dispatch the
      // same shape gd-cart-add.js does for a grid quick-add: { form, line }.
      // The `gd:cart-open` fallback is kept for exactly the case gd-cart-add.js
      // keeps it — if the toast script is absent or bailed, the shopper must
      // still get a confirmation rather than silence.
      const detail = { form: elTray, line: body };
      ownAdd = true;
      try {
        document.body.dispatchEvent(new CustomEvent('product-added-to-cart', { detail }));
        const owned = !!(window.gdCartToast && window.gdCartToast.handles(detail));
        if (!owned) document.body.dispatchEvent(new CustomEvent('gd:cart-open'));
      } finally { ownAdd = false; }
    } catch (err) {
      console.warn('[gd-igc] tray add failed', err);
      emit('instant_gels_tray_atc_error', { message: String(err && err.message) });
      busy = false; render();
      // In-tray, auto-dismissing, and localized — a native alert() blocks the
      // page and showed English "Load failed" to NO/SE/DK/FI shoppers.
      flash((err && err.message) || L.cartError);
      return;
    }
    busy = false; render();
  }

  /* ── convert loose singles into a kit ───────────────────────────────────
     ORDER OF OPERATIONS IS THE SAFETY PROPERTY. The kit is ADDED first, in one
     atomic /cart/add.js that Shopify accepts whole or not at all; the loose
     lines are removed only after that succeeds. If the removal then fails the
     shopper is left holding a kit AND their old singles — too much in the bag,
     visible, and fixable by them. The reverse order can leave an empty bag
     mid-purchase, which is the one outcome that must be impossible.

     Two theme-wide flags are respected. __gdCheckingOut is set by the checkout
     interceptor (gd-tier-gifts.js:411) and means a navigate is imminent —
     never start a swap into that. __gdCartProcessing is the shared mutex the
     sanitizer and both gift reconcilers take (gd-cart-sanitizer.js:53-68,
     gd-gift-with-purchase.js:49); holding it for the whole swap keeps them out
     of the window where the kit parent exists but the singles still do.

     Removal is KEYED, never positional: /cart/update.js addressed by line key
     is the only re-index-immune primitive in the theme (the shape used by
     gd-tier-gifts.js:183). A positional update would hit the wrong line the
     moment any other engine changed the cart first. */
  async function convertToKit() {
    if (busy) return;
    const o = offer();
    if (!o) return;
    if (window.__gdCheckingOut) { flash(L.offerBusy || L.cartError); return; }
    if (window.__gdCartProcessing) { flash(L.offerBusy || L.cartError); return; }
    window.__gdCartProcessing = true;
    busy = true; render();
    emit('instant_gels_kit_convert_click', { tier_id: o.tier.variantId, designs: o.chosen.length, save: o.save });
    let added = false;
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: offerPayload(o) }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.description || body.message || L.cartError);
      added = true;

      // Decrement each consumed line by exactly the number of units the kit took.
      const dec = new Map();
      o.consume.forEach(x => dec.set(x.key, (dec.get(x.key) || 0) + 1));
      const updates = {};
      dec.forEach((n, key) => { updates[key] = Math.max(0, (lineQty.get(key) || 0) - n); });
      if (Object.keys(updates).length) {
        const u = await fetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates }),
        });
        // A failed removal is NOT a failed conversion. The kit is in the bag and
        // paid-for correctly; say so rather than rolling back a good add.
        if (!u.ok) console.warn('[gd-igc] kit added but loose lines not removed');
      }
      await dropPaidDupes(o.tier.extras);

      // Tray picks that went into the kit are spent. Ones the cart already
      // covered were never in the tray, so this only removes what it should.
      o.chosen.forEach(id => { const i = kit.indexOf(id); if (i >= 0) kit.splice(i, 1); });
      saveKit();
      emit('instant_gels_kit_convert_success', { tier_id: o.tier.variantId, save: o.save });
      await readOwned();
      ownAdd = true;
      try {
        const detail = { form: elTray, line: null };
        document.body.dispatchEvent(new CustomEvent('product-added-to-cart', { detail }));
        document.body.dispatchEvent(new CustomEvent('cart:updated'));
      } finally { ownAdd = false; }
    } catch (err) {
      console.warn('[gd-igc] kit convert failed', err);
      emit('instant_gels_kit_convert_error', { message: String(err && err.message), added: added });
      flash((err && err.message) || L.cartError);
    } finally {
      window.__gdCartProcessing = false;
      busy = false; render();
    }
  }

  if (elTray) {
    elTray.addEventListener('click', e => {
      if (busy) return;
      const rm = e.target.closest('[data-slot-remove]');
      if (rm) { e.stopPropagation(); toggleKit(+rm.dataset.slotRemove); return; }
      const op = e.target.closest('[data-slot-open]');
      if (op) { openQv(+op.dataset.slotOpen); return; }
      if (e.target.closest('[data-tray-convert]')) { convertToKit(); return; }
      if (e.target.closest('[data-tray-clear]')) { kit.length = 0; saveKit(); render(); return; }
      if (e.target.closest('[data-tray-cta]')) submitTray();
    });
  }

  /* ── events ─────────────────────────────────────────────────────────── */
  elChips.addEventListener('click', e => {
    const c = e.target.closest('[data-chip]');
    if (!c) return;
    state.chip = c.dataset.chip;
    render();
  });

  host.addEventListener('click', e => {
    if (e.target.closest('[data-igc-filters]')) { openDrawer(); return; }
    if (e.target.closest('[data-sort-toggle]')) { state.sortOpen = !state.sortOpen; renderSort(); return; }
    const so = e.target.closest('[data-sort]');
    if (so) { state.sort = so.dataset.sort; state.sortOpen = false; render(); return; }
    if (e.target.closest('[data-reset]')) { resetAll(); return; }
  });
  // Outside-click closes the sort menu. Test the button's OWN attributes, not
  // its position in the tree: renderSort() replaces the markup while this same
  // click is still bubbling, so by the time it reaches document the clicked
  // node is detached and a closest('[data-igc-sort]') ancestor test fails —
  // which made the menu open and shut on one click.
  document.addEventListener('click', e => {
    if (!mine() || !state.sortOpen) return;
    if (e.target.closest('[data-sort-toggle],[data-sort],[data-igc-sort]')) return;
    state.sortOpen = false; renderSort();
  });

  /* ── swipe the card gallery (touch) ─────────────────────────────────────
     The arrows are hidden on touch devices, so this is how a phone browses a
     card's images. Deliberately conservative:
       · listeners are PASSIVE and never call preventDefault, so vertical
         scrolling stays entirely the browser's — a gallery must never be able
         to make the page feel stuck;
       · only a clearly horizontal gesture counts (>30px and dominating the
         vertical), so an ordinary tap, which moves ~0px, can never trip it;
       · a real swipe suppresses the open for a moment, because on some
         browsers a swipe still ends in a click and the popup would open on
         top of the image the shopper was trying to look at.
     Single-touch only: a two-finger pinch-zoom is the browser's, not ours. */
  let swipedAt = 0;
  (() => {
    let sx = 0, sy = 0, id = null;
    elGrid.addEventListener('touchstart', e => {
      id = null;
      if (e.touches.length !== 1) return;
      const m = e.target.closest('[data-media]');
      if (!m) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; id = +m.dataset.media;
    }, { passive: true });
    elGrid.addEventListener('touchend', e => {
      if (id === null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - sx, dy = t.clientY - sy;
      const target = id; id = null;
      if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const d = byId(target), n = galOf(d).length;
      if (n < 2) return;
      state.imgIdx[target] = (((state.imgIdx[target] || 0) + (dx < 0 ? 1 : -1)) % n + n) % n;
      swipedAt = Date.now();
      render();
    }, { passive: true });
  })();

  elGrid.addEventListener('click', e => {
    const nav = e.target.closest('[data-nav]');
    if (nav) {
      e.preventDefault(); e.stopPropagation();
      const id = +nav.dataset.id, d = byId(id), n = galOf(d).length;
      state.imgIdx[id] = (((state.imgIdx[id] || 0) + (+nav.dataset.nav)) % n + n) % n;
      render(); return;
    }
    const dot = e.target.closest('[data-dot]');
    if (dot) { e.preventDefault(); e.stopPropagation(); state.imgIdx[+dot.dataset.id] = +dot.dataset.dot; render(); return; }
    // "+" toggles the tray without opening quick view.
    const add = e.target.closest('[data-add]');
    if (add) { e.preventDefault(); e.stopPropagation(); toggleKit(+add.dataset.add); return; }
    // Card opens quick view. The media is still a real <a href> so middle-click,
    // cmd-click and crawlers all reach the product page normally — we only
    // intercept the plain left click.
    const media = e.target.closest('[data-media]');
    if (media && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      // A swipe that ends in a synthetic click must not also open the modal.
      if (Date.now() - swipedAt < 400) return;
      openQv(+media.dataset.media);
    }
  });

  restoreKit();
  render();

  /* Cart awareness. Read-only, and deliberately after the first paint: the grid
     must never wait on a cart round-trip to render. Re-read on every signal the
     theme already emits for a cart change, plus bfcache restores — a shopper
     who checked out in another tab and came back with the back button would
     otherwise be shown an offer against a cart that no longer exists. */
  if (trayOn) {
    let t = 0;
    const refresh = () => { clearTimeout(t); t = setTimeout(() => readOwned().then(render), 250); };
    readOwned().then(render);
    ['cart:updated', 'product-added-to-cart', 'gd:cart-refreshed'].forEach(ev => {
      document.body.addEventListener(ev, refresh);
    });
    window.addEventListener('pageshow', e => { if (e.persisted) refresh(); });
  }

  window.gdIgc = { state, render, DESIGNS, kit, plan, offer, readOwned, ownedUnits: () => owned };
})();
